import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  clearPartnerProfilePhoto,
  getPartnerProfileWorkspace,
  updatePartnerAccount,
  updatePartnerProfile,
  uploadPartnerProfilePhoto,
} from "../../api/partnerPortalApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import { useLocale } from "../../intl";
import { resolvePublicImageUrl } from "../../utils/resolvePublicImageUrl";
import PartnerCasaMapaSettingsShell from "./PartnerCasaMapaSettingsShell";
import {
  formatPartnerSettingsWhen,
  PARTNER_SETTINGS_COPY as COPY,
} from "./partnerCasaMapaSettingsShared";

function initialsFrom(profile, account) {
  const first = profile?.firstName || "";
  const last = profile?.lastName || "";
  if (first || last) return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase() || "P";
  const name = account?.displayName || account?.businessName || "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name).slice(0, 2).toUpperCase();
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = () => reject(new Error("Could not read the selected image."));
    reader.readAsDataURL(file);
  });
}

function activityTone(eventType) {
  if (String(eventType || "").includes("lead")) return "lead";
  if (String(eventType || "").includes("inventory") || String(eventType || "").includes("contrib")) {
    return "inventory";
  }
  if (String(eventType || "").includes("photo")) return "photo";
  return "default";
}

export default function PartnerCasaMapaProfilePage() {
  const { t } = useLocale();
  const { path } = usePartnerPortalBase();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [completeness, setCompleteness] = useState(0);
  const [checklist, setChecklist] = useState([]);
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const hashTarget =
    location.hash === "#settings-inventory"
      ? path("profile/inventory")
      : location.hash === "#settings-crm"
        ? path("profile/crm")
        : null;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await getPartnerProfileWorkspace();
      setProfile(body.profile);
      setAccount(body.account);
      setCompleteness(body.completeness || 0);
      setChecklist(body.checklist || []);
      setActivities(body.activities || []);
      setSummary(body.summary || {});
    } catch (err) {
      setError(err.message || t(`${COPY}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const photoUrl = resolvePublicImageUrl(account?.profilePhotoUrl || account?.logoUrl || "");
  const displayName =
    account?.displayName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ") ||
    account?.businessName ||
    t(`${COPY}.account.fallbackName`);
  const localizedChecklist = useMemo(
    () =>
      checklist.map((item) => {
        const base = `${COPY}.account.checklist`;
        if (item.id === "leads" && Number(summary.newLeads || 0) > 0) {
          return {
            ...item,
            label: t(`${base}.leadsRespond`),
            actionLabel: t(`${base}.leadsRespondAction`),
          };
        }
        const labelKey = `${base}.${item.id}`;
        const actionKey = `${base}.${item.id}Action`;
        return {
          ...item,
          label: t(labelKey) !== labelKey ? t(labelKey) : item.label,
          actionLabel: t(actionKey) !== actionKey ? t(actionKey) : item.actionLabel,
        };
      }),
    [checklist, summary.newLeads, t]
  );
  const openChecklist = useMemo(
    () => localizedChecklist.filter((item) => !item.done),
    [localizedChecklist]
  );
  const nextAction = openChecklist[0] || null;

  const saveAll = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      await Promise.all([
        updatePartnerProfile(profile),
        updatePartnerAccount({
          displayName: account.displayName,
          bio: account.bio,
          phone: account.phone || profile?.phone,
          websiteUrl: account.websiteUrl || "",
          // Photo is managed by the dedicated upload endpoints.
        }),
      ]);
      setMessage(t(`${COPY}.account.profileSaved`));
      await load();
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setSaving(false);
    }
  };

  const onPickPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPhotoBusy(true);
    setMessage("");
    setError("");
    try {
      const base64 = await readFileAsBase64(file);
      const result = await uploadPartnerProfilePhoto({
        base64,
        fileName: file.name,
        mimeType: file.type,
      });
      setAccount((prev) => ({
        ...(prev || {}),
        profilePhotoUrl: result.profilePhotoUrl || result.account?.profilePhotoUrl,
      }));
      setMessage(result.message || t(`${COPY}.account.photoSaved`));
      await load();
    } catch (err) {
      setError(err.message || t(`${COPY}.account.photoError`));
    } finally {
      setPhotoBusy(false);
    }
  };

  const onRemovePhoto = async () => {
    setPhotoBusy(true);
    setError("");
    try {
      await clearPartnerProfilePhoto();
      setAccount((prev) => ({ ...(prev || {}), profilePhotoUrl: null }));
      setMessage(t(`${COPY}.account.photoRemoved`));
      await load();
    } catch (err) {
      setError(err.message || t(`${COPY}.account.photoError`));
    } finally {
      setPhotoBusy(false);
    }
  };

  if (hashTarget) {
    return <Navigate to={hashTarget} replace />;
  }

  if (loading && !profile) {
    return <p className="muted panel-card">{t(`${COPY}.loading`)}</p>;
  }

  return (
    <PartnerCasaMapaSettingsShell
      title={t(`${COPY}.account.heroTitle`)}
      subtitle={t(`${COPY}.account.heroSubtitle`)}
      message={message}
      error={error}
    >
      <section className="cm-partner-profile-hero" aria-label={t(`${COPY}.account.identityLabel`)}>
        <div className="cm-partner-profile-hero__identity">
          <div className="cm-partner-profile-hero__photo-wrap">
            {photoUrl ? (
              <img className="cm-partner-profile-hero__photo" src={photoUrl} alt="" />
            ) : (
              <div className="cm-partner-profile-hero__photo-fallback" aria-hidden="true">
                {initialsFrom(profile, account)}
              </div>
            )}
            <div
              className="cm-partner-profile-hero__ring"
              style={{ "--cm-profile-complete": `${Math.min(100, completeness)}%` }}
              aria-hidden="true"
            />
          </div>
          <div className="cm-partner-profile-hero__copy">
            <p className="cm-partner-profile-hero__kicker">{t(`${COPY}.account.completeness`, { pct: completeness })}</p>
            <h2>{displayName}</h2>
            <p className="cm-partner-profile-hero__meta">
              {profile?.email || account?.email || "—"}
              {account?.partnerType ? ` · ${account.partnerType}` : ""}
            </p>
            <div className="cm-partner-settings__actions">
              <button
                type="button"
                className="partner-dashboard-btn partner-dashboard-btn--primary"
                disabled={photoBusy}
                onClick={() => fileInputRef.current?.click()}
              >
                {photoBusy
                  ? t(`${COPY}.account.photoUploading`)
                  : photoUrl
                    ? t(`${COPY}.account.changePhoto`)
                    : t(`${COPY}.account.addPhoto`)}
              </button>
              {photoUrl ? (
                <button
                  type="button"
                  className="partner-dashboard-btn"
                  disabled={photoBusy}
                  onClick={onRemovePhoto}
                >
                  {t(`${COPY}.account.removePhoto`)}
                </button>
              ) : null}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={onPickPhoto}
              />
            </div>
          </div>
        </div>

        <div className="cm-partner-profile-hero__stats" aria-label={t(`${COPY}.account.statsLabel`)}>
          <article>
            <span>{t(`${COPY}.account.statLeads`)}</span>
            <strong>{summary.newLeads ?? 0}</strong>
            <small>{t(`${COPY}.account.statLeadsHint`)}</small>
          </article>
          <article>
            <span>{t(`${COPY}.account.statInventory`)}</span>
            <strong>{summary.casamapaListingCount ? t(`${COPY}.account.statInventoryYes`) : "—"}</strong>
            <small>{t(`${COPY}.account.statInventoryHint`)}</small>
          </article>
          <article>
            <span>{t(`${COPY}.account.statComplete`)}</span>
            <strong>{completeness}%</strong>
            <small>{t(`${COPY}.account.statCompleteHint`)}</small>
          </article>
        </div>
      </section>

      {nextAction ? (
        <section className="cm-partner-profile-next" aria-label={t(`${COPY}.account.nextLabel`)}>
          <div>
            <p className="cm-partner-settings__eyebrow">{t(`${COPY}.account.nextKicker`)}</p>
            <h2>{nextAction.label}</h2>
            <p>{t(`${COPY}.account.nextBody`)}</p>
          </div>
          {nextAction.id === "photo" ? (
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              disabled={photoBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              {nextAction.actionLabel}
            </button>
          ) : nextAction.id === "bio" || nextAction.id === "website" ? (
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              onClick={() =>
                document.getElementById("profile-details")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
              }
            >
              {nextAction.actionLabel}
            </button>
          ) : (
            <Link
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              to={path(nextAction.actionPath)}
            >
              {nextAction.actionLabel}
            </Link>
          )}
        </section>
      ) : (
        <section className="cm-partner-profile-next cm-partner-profile-next--done">
          <div>
            <p className="cm-partner-settings__eyebrow">{t(`${COPY}.account.nextKicker`)}</p>
            <h2>{t(`${COPY}.account.allDoneTitle`)}</h2>
            <p>{t(`${COPY}.account.allDoneBody`)}</p>
          </div>
          <Link className="partner-dashboard-btn partner-dashboard-btn--primary" to={path("leads")}>
            {t(`${COPY}.account.openLeads`)}
          </Link>
        </section>
      )}

      <div className="cm-partner-profile-layout">
        <section className="cm-partner-settings__section">
          <div className="cm-partner-settings__section-head">
            <h2>{t(`${COPY}.account.checklistTitle`)}</h2>
            <p>{t(`${COPY}.account.checklistSubtitle`)}</p>
          </div>
          <ul className="cm-partner-profile-checklist">
            {localizedChecklist.map((item) => (
              <li key={item.id} className={item.done ? "is-done" : undefined}>
                <span className="cm-partner-profile-checklist__mark" aria-hidden="true">
                  {item.done ? "✓" : "○"}
                </span>
                <div>
                  <strong>{item.label}</strong>
                </div>
                {!item.done ? (
                  <Link
                    className="cm-partner-profile-checklist__cta"
                    to={
                      item.id === "photo" || item.id === "bio" || item.id === "website"
                        ? "#profile-details"
                        : path(item.actionPath)
                    }
                    onClick={
                      item.id === "photo"
                        ? (event) => {
                            event.preventDefault();
                            fileInputRef.current?.click();
                          }
                        : item.id === "bio" || item.id === "website"
                          ? (event) => {
                              event.preventDefault();
                              document.getElementById("profile-details")?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          : undefined
                    }
                  >
                    {item.actionLabel}
                  </Link>
                ) : (
                  <span className="cm-partner-settings__badge cm-partner-settings__badge--ok">
                    {t(`${COPY}.account.done`)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section className="cm-partner-settings__section">
          <div className="cm-partner-settings__section-head">
            <h2>{t(`${COPY}.account.activityTitle`)}</h2>
            <p>{t(`${COPY}.account.activitySubtitle`)}</p>
          </div>
          {activities.length === 0 ? (
            <p className="cm-partner-settings__muted">{t(`${COPY}.account.activityEmpty`)}</p>
          ) : (
            <ol className="cm-partner-profile-activity">
              {activities.map((row) => (
                <li key={row.eventId} data-tone={activityTone(row.eventType)}>
                  <div className="cm-partner-profile-activity__dot" aria-hidden="true" />
                  <div className="cm-partner-profile-activity__body">
                    <strong>{row.title}</strong>
                    {row.detail ? <p>{row.detail}</p> : null}
                    <span>{formatPartnerSettingsWhen(row.createdAt) || "—"}</span>
                    {row.actionPath ? (
                      <Link to={path(row.actionPath)}>{t(`${COPY}.account.activityOpen`)}</Link>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      <section className="cm-partner-settings__section" id="profile-details">
        <div className="cm-partner-settings__section-head">
          <h2>{t(`${COPY}.account.detailsTitle`)}</h2>
          <p>{t(`${COPY}.account.detailsSubtitle`)}</p>
        </div>

        {profile ? (
          <div className="cm-partner-settings__grid">
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.account.firstName`)}</span>
              <input
                value={profile.firstName || ""}
                onChange={(event) => setProfile({ ...profile, firstName: event.target.value })}
              />
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.account.lastName`)}</span>
              <input
                value={profile.lastName || ""}
                onChange={(event) => setProfile({ ...profile, lastName: event.target.value })}
              />
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.account.phone`)}</span>
              <input
                value={profile.phone || account?.phone || ""}
                onChange={(event) => {
                  const phone = event.target.value;
                  setProfile({ ...profile, phone });
                  setAccount({ ...(account || {}), phone });
                }}
              />
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.account.email`)}</span>
              <input value={profile.email || ""} disabled readOnly />
            </label>
          </div>
        ) : null}

        {account ? (
          <div className="cm-partner-settings__grid">
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.account.displayName`)}</span>
              <input
                value={account.displayName || ""}
                onChange={(event) => setAccount({ ...account, displayName: event.target.value })}
              />
            </label>
            <label className="cm-partner-settings__field">
              <span>{t(`${COPY}.account.website`)}</span>
              <input
                value={account.websiteUrl || ""}
                onChange={(event) => setAccount({ ...account, websiteUrl: event.target.value })}
              />
            </label>
            <label className="cm-partner-settings__field" style={{ gridColumn: "1 / -1" }}>
              <span>{t(`${COPY}.account.bio`)}</span>
              <textarea
                value={account.bio || ""}
                rows={5}
                placeholder={t(`${COPY}.account.bioPlaceholder`)}
                onChange={(event) => setAccount({ ...account, bio: event.target.value })}
              />
            </label>
          </div>
        ) : null}

        <div className="cm-partner-settings__actions">
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            disabled={saving}
            onClick={saveAll}
          >
            {saving ? t(`${COPY}.saving`) : t(`${COPY}.account.saveAll`)}
          </button>
        </div>
      </section>
    </PartnerCasaMapaSettingsShell>
  );
}
